import { useAppStore } from '@/dashboard/store/appStore';
import { useWorkspaceAddAndEdit } from '@/dashboard/store/workspaceAddEditStore';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import { CurrentUser } from '@/dashboard/types/current_user';
import { Step, StepType } from '@/dashboard/types/step';
import { getWorkspaceVisibilityType, WorkspaceVisibility } from '@/dashboard/types/workspace_visibility';
import TeamUser from '@/dashboard/types/teamUser';
import { Workspace } from '@/dashboard/types/workspace';
import { UserRole } from '@/dashboard/types/user_role';

export function useCreateWorkspacePopup() {
  const appStore = useAppStore();
  const workspaceAddAndEdit = useWorkspaceAddAndEdit();

  const { currentUser: user } = storeToRefs(appStore);
  const { showPopup, isCreate, workspace, step, tabs } = storeToRefs(workspaceAddAndEdit);

  const currentUser: ComputedRef<CurrentUser | undefined> = computed(() => {
    if (isCreate.value) {
      return { ...user.value, role: 'owner' } as CurrentUser;
    } else {
      return appStore.currentWorkspaceUser as CurrentUser;
    }
  });
  const showTeamMenu: ComputedRef<boolean> = computed(() => type.value === 'team');
  const showPrivate: ComputedRef<boolean> = computed(() => team.value.length <= 1);
  const buttonText: ComputedRef<string> = computed(() => {
    if (!isCreate.value) {
      return 'Submit';
    } else if (isCreate.value && step.value === Step.Name && type.value !== 'private') {
      return 'Next';
    } else {
      return 'Create';
    }
  });
  const type: Ref<WorkspaceVisibility> = ref('team');
  const name: Ref<string> = ref('');
  const nameError: Ref<boolean> = ref(false);
  const team: Ref<TeamUser[]> = ref([]);

  function closePopup() {
    workspaceAddAndEdit.closePopup();
  }

  function validateName() {
    const text = name.value.trim();

    if (text.length <= 0) {
      nameError.value = true;
      workspaceAddAndEdit.setStep(Step.Name);
      return false;
    } else {
      nameError.value = false;
      return true;
    }
  }

  function onTeamUpdated(updatedTeam: TeamUser[]) {
    team.value = updatedTeam;
  }

  function setStep(step: StepType) {
    workspaceAddAndEdit.setStep(step);
  }

  async function submitAction() {
    if (!validateName()) {
      return;
    }

    const shortLink = workspace.value?.short_link;
    let responseWorkspace: Workspace | undefined = undefined;
    const create = isCreate.value;

    if (create) {
      if (step.value === Step.Name && type.value !== 'private') {
        workspaceAddAndEdit.setStep(Step.Team);
        return;
      }

      responseWorkspace = await workspaceAddAndEdit.create(name.value, team.value, type.value);
    } else if (shortLink) {
      responseWorkspace = await workspaceAddAndEdit.edit(name.value, team.value, shortLink, type.value);
    }

    if (responseWorkspace) {
      if (responseWorkspace.id === workspace.value?.id) {
        appStore.setWorkspace(responseWorkspace);
      }

      await appStore.loadWorkspaces();
    }
  }

  watch(showPopup, () => {
    const currentWorkspace = workspace.value;

    if (showPopup.value && !!currentWorkspace && !!currentWorkspace.users) {
      name.value = currentWorkspace.name;
      type.value = getWorkspaceVisibilityType(currentWorkspace);
      team.value = currentWorkspace.users.map((item) => {
        return new TeamUser(item.email, item.role, 'workspace', item.image, true);
      });
    } else if (showPopup.value && currentUser.value) {
      const user = new TeamUser(currentUser.value.email, UserRole.Owner, 'workspace', currentUser.value.image, true);
      team.value = [user];
      name.value = 'New workspace';
      type.value = 'team';
    }
  });

  return {
    type,
    isCreate,
    currentUser,
    buttonText,
    step,
    workspace,
    showPopup,
    name,
    nameError,
    showTeamMenu,
    showPrivate,
    team,
    tabs,
    onTeamUpdated,
    submitAction,
    closePopup,
    setStep,
  };
}
